
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import type { ReactNode } from 'react';
import { useApiCall } from '@/hooks/useApi';
import { getCardImages, type CardEntityType, type CardImage } from '@/api/cardImages';

interface CardImagesContextValue {
  getCustomImage: (entityType: CardEntityType, entityId: string) => string | undefined;
  setCustomImage: (entityType: CardEntityType, entityId: string, imageUrl: string) => void;
  removeCustomImage: (entityType: CardEntityType, entityId: string) => void;
}

const CardImagesContext = createContext<CardImagesContextValue | undefined>(undefined);

export const CardImagesProvider = ({ children }: { children: ReactNode }) => {
  const [imageMap, setImageMap] = useState<Map<string, string>>(new Map());

  const { data: cardImages } = useApiCall<CardImage[]>(getCardImages, [], {
    immediate: true,
    showErrorToast: false,
  });

  useEffect(() => {
    if (!cardImages) {
      return;
    }
    const map = new Map<string, string>();
    for (const img of cardImages) {
      map.set(`${img.entity_type}:${img.entity_id}`, img.image_url);
    }
    setImageMap(map);
  }, [cardImages]);

  const getCustomImage = useCallback(
    (entityType: CardEntityType, entityId: string): string | undefined => {
      return imageMap.get(`${entityType}:${entityId}`);
    },
    [imageMap]
  );

  const setCustomImage = useCallback(
    (entityType: CardEntityType, entityId: string, imageUrl: string) => {
      setImageMap((prev) => {
        const next = new Map(prev);
        next.set(`${entityType}:${entityId}`, imageUrl);
        return next;
      });
    },
    []
  );

  const removeCustomImage = useCallback(
    (entityType: CardEntityType, entityId: string) => {
      setImageMap((prev) => {
        const next = new Map(prev);
        next.delete(`${entityType}:${entityId}`);
        return next;
      });
    },
    []
  );

  const value = useMemo(
    () => ({ getCustomImage, setCustomImage, removeCustomImage }),
    [getCustomImage, setCustomImage, removeCustomImage]
  );

  return (
    <CardImagesContext.Provider value={value}>
      {children}
    </CardImagesContext.Provider>
  );
};

export const useCardImages = (): CardImagesContextValue => {
  const context = useContext(CardImagesContext);
  if (!context) {
    throw new Error('useCardImages must be used within a CardImagesProvider');
  }
  return context;
};
